// =============================================================================
// AuthFooter — quiet footer shared by all Platform auth pages.
//
// Surfaces the Platform marketing + legal pages a first-time Platform
// visitor needs to evaluate signing up. All targets are Platform-hosted
// (relative paths) so the content stays on the Platform domain rather
// than bouncing visitors out to the consumer site — different audience,
// different pitch.
//
//   /features       — what the Platform does (admin console, white-label,
//                     per-plan gating, Stripe Connect, etc.)
//   /about          — about DiscoveryGem Platform + Arrive-Tech LLC
//   /legal/*.html   — Privacy / Terms / Cookies / Acceptable Use (legal
//                     docs are the same company-level docs as the consumer
//                     side; rehosted on the Platform domain so a Platform
//                     visitor never has to cross domains to read terms).
//
// One link out to the consumer DiscoveryGem app (https://discoverygem.com)
// so a curious visitor can still see what end-users get.
//
// Internal links navigate in the same tab — the user is just browsing
// pre-signup. External link (consumer app) opens new tab so a half-filled
// signup form isn't lost.
// =============================================================================

interface FooterLink {
  label:    string;
  href:     string;
  external?: boolean;
}

const PRODUCT_LINKS: FooterLink[] = [
  { label: 'Features',     href: '/features' },
  { label: 'About',        href: '/about' },
  { label: 'Consumer app', href: 'https://discoverygem.com', external: true },
];

const LEGAL_LINKS: FooterLink[] = [
  { label: 'Privacy',         href: '/legal/privacy.html' },
  { label: 'Terms',           href: '/legal/terms.html' },
  { label: 'Cookies',         href: '/legal/cookies.html' },
  { label: 'Acceptable Use',  href: '/legal/acceptable-use.html' },
];

// Social handles. Update these if the actual brand accounts differ —
// they're inferred from the planitshareitliveit brand and match what we
// expect a B2B SaaS visitor to look for (LinkedIn first for B2B).
interface SocialLink {
  label: string;
  href:  string;
  svg:   React.ReactNode;
}

const SOCIAL_LINKS: SocialLink[] = [
  {
    label: 'LinkedIn',
    href:  'https://www.linkedin.com/company/planitshareitliveit',
    svg: (
      <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className="w-4 h-4">
        <path d="M20.452 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.356V9h3.414v1.561h.049c.476-.9 1.637-1.852 3.37-1.852 3.602 0 4.267 2.37 4.267 5.455v6.288zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.063 2.063 2.063 0 1 1 2.063 2.063zm1.778 13.019H3.555V9h3.56v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
      </svg>
    ),
  },
  {
    label: 'X',
    href:  'https://x.com/planitshareitliveit',
    svg: (
      <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className="w-4 h-4">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
      </svg>
    ),
  },
  {
    label: 'Instagram',
    href:  'https://www.instagram.com/planitshareitliveit',
    svg: (
      <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className="w-4 h-4">
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z"/>
      </svg>
    ),
  },
  {
    label: 'TikTok',
    href:  'https://www.tiktok.com/@planitshareitliveit',
    svg: (
      <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className="w-4 h-4">
        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.84-.1z"/>
      </svg>
    ),
  },
  {
    label: 'YouTube',
    href:  'https://www.youtube.com/@planitshareitliveit',
    svg: (
      <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className="w-4 h-4">
        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
      </svg>
    ),
  },
];

export function AuthFooter() {
  return (
    <footer className="px-6 pb-6 pt-4 text-[11px] text-surface-500">
      <div className="max-w-5xl mx-auto flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          {PRODUCT_LINKS.map(l => (
            <a
              key={l.href}
              href={l.href}
              {...(l.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
              className="hover:text-surface-700 transition-colors"
            >
              {l.label}
            </a>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          {LEGAL_LINKS.map(l => (
            <a
              key={l.href}
              href={l.href}
              className="hover:text-surface-700 transition-colors"
            >
              {l.label}
            </a>
          ))}
        </div>
      </div>
      <p className="max-w-5xl mx-auto mt-3 text-[10px] text-surface-400">
        © 2026 DiscoveryGem Platform · Operated by Arrive-Tech LLC.
      </p>

      {/* Social row — small icon strip at the very bottom. New tab on
          all because they exit to external networks; if a sign-up form
          is half-filled we don't want to lose it. */}
      <div className="max-w-5xl mx-auto mt-3 flex items-center gap-4 text-surface-400">
        {SOCIAL_LINKS.map(s => (
          <a
            key={s.href}
            href={s.href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={s.label}
            className="hover:text-surface-700 transition-colors"
          >
            {s.svg}
          </a>
        ))}
      </div>
    </footer>
  );
}
